import { createSlice } from '@reduxjs/toolkit';
import API_CONFIG from '../../config/apiConstant';
import { clearJwt, isTokenValid } from '../../helpers/handleJwt';
import CookieHelper from '../../helpers/cookieHelper';
import logger from '../../helpers/logger';

const initialState = {
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
};

// Load token + user from cookie/localStorage on page refresh
const loadUserFromStorage = () => {
    try {
        const cookieHelper = new CookieHelper();

        // Try to load from cookies
        const cookieUser = cookieHelper.getUserCookie();
        if (cookieUser && cookieUser.token) {
            
            // Validate token
            if (!isTokenValid()) {
                clearJwt();
                return null;
            }
            return { user: cookieUser, token: cookieUser.token };
        }

        // Fallback to localStorage
        const localUser = localStorage.getItem(API_CONFIG.STORAGE_KEYS.USER);
        const localToken = localStorage.getItem(API_CONFIG.STORAGE_KEYS.ACCESS_TOKEN);

        if (localUser && localToken) {
            
            // Validate token
            if (!isTokenValid()) {
                clearJwt();
                return null;
            }
            return {
                user: JSON.parse(localUser),
                token: localToken
            };
        }

    } catch (error) {
        logger.error("Error loading user from storage:", error);
        clearJwt();
    }

    return null;
};

// Initialize state with stored user data
const savedSession = loadUserFromStorage();
if (savedSession) {
    initialState.user = savedSession.user;
    initialState.token = savedSession.token;
    initialState.isAuthenticated = true;
}
initialState.isLoading = false;

const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {

        loginStart: (state) => {
            state.isLoading = true;
            state.error = null;
        },

        loginSuccess: (state, action) => {
            const { user, token } = action.payload;
            const cookieHelper = new CookieHelper();

            state.user = user;
            state.token = token;
            state.isAuthenticated = true;
            state.isLoading = false;

            // Store in localStorage + cookie
            localStorage.setItem(API_CONFIG.STORAGE_KEYS.USER, JSON.stringify(user));
            localStorage.setItem(API_CONFIG.STORAGE_KEYS.ACCESS_TOKEN, token);

            cookieHelper.setUserCookie({ ...user, token });
        },

        loginFailure: (state, action) => {
            state.error = action.payload;
            state.isLoading = false;
        },

        logout: (state) => {
            state.user = null;
            state.token = null;
            state.isAuthenticated = false;

            clearJwt();
        },
    }
});

export const { loginStart, loginSuccess, loginFailure, logout } = authSlice.actions;

export const selectUser = (state) => state.auth.user;
export const selectToken = (state) => state.auth.token;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;

export default authSlice.reducer;
